import { Family, FamilyDisplayProperties, NewFamily } from "./ResearchDataModel"
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedKeyList } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import _, { uniqBy } from "lodash"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { HistoricalDate } from './HistoricalDate'
import { SortDate } from './SortDate'
import { PatchChange } from '@webapp/util/Api'
import { usePlaceStore } from './PlaceStore'
import { useViewFamilyStore } from './ViewFamilyStore'
import { useViewPersonStore } from './ViewPersonStore'
import { useAssertionStore } from "./AssertionStore"

export const useFamilyStore = defineStore("familyStore", () => {
    const asyncFamilies = new Map<string, AsyncData<Family>>()
    const asyncAssertionKeys = new Map<string, AsyncData<VersionedKeyList>>()

    const rd = useResearchDataApi()
    const assertionStore = useAssertionStore()
    const placeStore = usePlaceStore()
    const viewFamilyStore = useViewFamilyStore()
    const viewPersonStore = useViewPersonStore()
    
    function getAsyncFamily(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncFamilyList([id], loadMode)[0] : undefined
    }

    function getAsyncFamilyList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useFamilyStore() // use to get/return reactive store objects
        const loadFamiliesAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainFamilies = await rd.getPlainByIdsAsync("families", idsToLoad)
            return processExpandedFamilies(plainFamilies, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncFamilies, ids, "Family", loadMode, loadFamiliesAsync)
    }

    async function getFamilyListAsync(ids: (string | undefined)[]) {
        const asyncItems = getAsyncFamilyList(ids, LoadMode.EnsureLoaded)
        return (await Promise.all(asyncItems.map(a => a.whenLoadCompleted))).filter(isDefined)
    }

    function getLoadedFamily(id: string | undefined) {
        return getLoadedFamilyList([id]).at(0)
    }

    function getLoadedFamilyList(ids: (string | undefined)[]) {
        const store = useFamilyStore() // use to get/return reactive store objects
        return ids.map(id => store.asyncFamilies.get(id ?? '')).map(a => a?.data).filter(isDefined)
    }

    function getAsyncAssertionKeyLists(familyIds: string[], loadMode = LoadMode.TrackOnly) {
        if (loadMode != LoadMode.TrackOnly) {
            throw `FamilyStore: Load mode not implemented`
        }
        // ensure all requested key lists are present
        const store = useFamilyStore() // use to get/return reactive store objects
        AsyncDataStore.getAsyncItemsToLoad(store.asyncAssertionKeys, familyIds, "Family.AssertionKeys", loadMode)
        // get all key lists (not just the ones that need loading)
        return familyIds.map(id => store.asyncAssertionKeys.get(id)!)
    }

    function getLoadedAssertionKeyLists(personIds: string[]) {
        const store = useFamilyStore() // use to get/return reactive store objects
        return personIds.map(id => store.asyncAssertionKeys.get(id)?.data).filter(isDefined)
    }

    function areDetailsLoaded(familyId: string) {
        const store = useFamilyStore() // use to get/return reactive store objects
        const p = store.asyncFamilies.get(familyId)
        if (!p || !p.isLoadComplete())
            return false

        const asyncAssertionKeyList = store.asyncAssertionKeys.get(familyId)
        if (!asyncAssertionKeyList)
            return false

        const assertionIds = asyncAssertionKeyList.data?.keys ?? []
        return assertionStore.areExpandedAssertionsLoaded(assertionIds)
    }

    async function loadDetailsAsync(familyId: string, loadMode = LoadMode.TrackOnly) {
        if (loadMode != LoadMode.Reload && areDetailsLoaded(familyId)) {
            console.log(`Details for family ${familyId} already loaded`)
            return
        }
        if (loadMode != LoadMode.TrackOnly) {
            const expand = "assertions/place,assertions/site"
            const requestTime = DateTime.utc()
            const plainFamilies = await rd.getPlainByIdsAsync("families", [familyId], expand)

            processExpandedFamilies(plainFamilies, requestTime)
        }
    }

    async function addAsync(groupId: string, family: NewFamily) {
        const store = useFamilyStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const p = await rd.postPlainAsync(`groups/${groupId}/families`, family)
            // HACK: add this because we expect it to be present, but a new family never has any data, so an empty object is "correct"
            p.displayProperties = {}
            return plainToFamily(p)
        }
        const newFamilyId = await AsyncDataStore.addToStoreMapAsync(store.asyncFamilies, "Family", f => f.id, addCoreAsync)

        // invalidate view persons for the new family's spouses (also invalidates their spouse view families)
        const viewPersonStore = useViewPersonStore()
        viewPersonStore.invalidate(family.fatherId)
        viewPersonStore.invalidate(family.motherId)

        return newFamilyId
    }

    async function patchAsync(id: string, changes: PatchChange[]) {
        const store = useFamilyStore() // use to get/return reactive store objects
        const asyncFam = store.asyncFamilies.get(id)
        if (!asyncFam)
            throw `Family ${id} not found in store`

        const patchCoreAsync = async (id: string, changes: PatchChange[]) => {
            const p = await rd.patchPlainAsync(`families/${id}`, changes);
            return plainToFamily(p);
        }
        await AsyncDataStore.patchFromStoreAsync(store.asyncFamilies, id, "Family", changes, patchCoreAsync)

        await invalidateViewAsync(asyncFam)
    }

    async function deleteAsync(id: string) {
        const store = useFamilyStore() // use to get/return reactive store objects
        const asyncFam = store.asyncFamilies.get(id)
        if (!asyncFam)
            throw `Family ${id} not found in store`

        await rd.deleteAsync(`families/${id}`)

        AsyncData.setDeleted(asyncFam)
        await invalidateViewAsync(asyncFam)
    }

    async function invalidateViewAsync(asyncFam: AsyncData<Family>) {
        const faVP = viewPersonStore.asyncViewPersons.get(asyncFam.data?.fatherId ?? '')?.data
        const moVP = viewPersonStore.asyncViewPersons.get(asyncFam.data?.motherId ?? '')?.data
        
        AsyncData.invalidate(viewFamilyStore.asyncViewFamilies.get(asyncFam.key))
        if (faVP?.id) {
            AsyncData.invalidate(viewPersonStore.asyncSpouseFamilyKeys.get(faVP.id))
        }
        if (moVP?.id) {
            AsyncData.invalidate(viewPersonStore.asyncSpouseFamilyKeys.get(moVP.id))
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function addFamiliesToStore(plainFamilies: any[], requestTime: DateTime) {
        const store = useFamilyStore() // use to get/return reactive store objects
        const familyMap = processExpandedFamilies(uniqBy(plainFamilies, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncFamilies, "Family", familyMap, requestTime)
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function processExpandedFamilies(plainFamilies: any[], requestTime: DateTime) {
        // NOTE: family display properties may be missing
        processExpandedFamilyDisplayProperties(plainFamilies.map(p => p.displayProperties).filter(isDefined), requestTime)
        return AsyncDataStore.createItemMap(plainFamilies, "Family", plainToFamily, f => f.id!)
    }

    function processExpandedFamilyDisplayProperties(plainDisplayProperties: any[], requestTime: DateTime) {
        placeStore.addPlacesToStore(plainDisplayProperties.map(p => p.marriagePlace).filter(isDefined), requestTime)
    }

    return {
        asyncFamilies,
        asyncAssertionKeys,

        getAsyncFamily,
        getAsyncFamilyList,
        getFamilyListAsync,
        getLoadedFamily,
        getLoadedFamilyList,
        getAsyncAssertionKeyLists,
        getLoadedAssertionKeyLists,
        areDetailsLoaded,
        loadDetailsAsync,
        addAsync,
        patchAsync,
        deleteAsync,

        addFamiliesToStore,
        processExpandedFamilies,
        processExpandedFamilyDisplayProperties,
    }
})

export function plainToFamily(p: any) {
    const family = assignExisting(new Family(), p)
    family.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
    family.displayProperties = plainToFamilyDisplayProperties(p.displayProperties)
    return family
}

export function plainToFamilyDisplayProperties(p: any) {
    const dp = new FamilyDisplayProperties()
    if (p) {
        assignExisting(dp, p)
        dp.marriageDate = HistoricalDate.parse(p.marriageDate)
        dp.marriageSortDate = SortDate.parse(p.marriageSortDate)
        dp.marriagePlaceId = p.marriagePlace?.id
    }
    return dp
}
